import Vue from 'vue';
import store from '@/vuex';
import { sync } from 'vuex-router-sync';
import App from './App.vue';
import router from './router';
import { createVuetify } from './plugins/vuetify';
// Regras globais do layout legado (deep-layout), importadas UMA vez.
// Antes viviam no _index.scss, que o Vite injeta em cada um dos 431 SFCs.
import '@/static/styles/_global.scss';
// Design System — por último, para vencer o vuetify.min.css e o legado na
// cascata. Traz fontes da marca, tokens --tc-* e o override dos componentes.
import '@/design-system';
import { installBrandSync } from '@/design-system/theme/sync';
import VueMask from 'v-mask';
import money from 'v-money';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import utilsStorage from '@/utils/storage';
import VueApexCharts from 'vue-apexcharts';
import '@/assets/css/tinymce-overrides.css';

// Gridstack styles
import 'gridstack/dist/gridstack.min.css';

import AccessDirective from '@/directives/access-directive.js';
import CorbanAccessDirective from '@/directives/corban-access-directive.js';
import AdministrativeAccessDirective from '@/directives/administrative-access-directive.js';
import AutoSelectSingleDirective from '@/directives/auto-select-single-directive.js';

import { ACCESS } from '../../common/constants/global';
import './registerServiceWorker';
import { loadTheme } from '@/utils/theme-loader';
import ToastPlugin from '@/plugins/toast';

// Registrar componentes Phosphor
import { registerPhosphorIcons } from '@/plugins/phosphor-icons.js';
registerPhosphorIcons();

Vue.config.productionTip = false;

Vue.prototype.$eventBus = new Vue();

Vue.prototype.$access = ACCESS;

Vue.use(VueMask);
Vue.use(money, { precision: 2 });

Vue.use(mixpanel);

Vue.use(VueApexCharts);
Vue.use(ToastPlugin);

Vue.directive('disabledIconFocus', {
  bind(el) {
    disableIconFocus(el);
  },
  componentUpdated(el) {
    disableIconFocus(el);
  }
});

Vue.directive('access', AccessDirective);
Vue.directive('corban-access', CorbanAccessDirective);
Vue.directive('administrative-access', AdministrativeAccessDirective);
Vue.directive('auto-select-single', AutoSelectSingleDirective);

function disableIconFocus(el) {
  el.querySelectorAll('.v-input__icon button').forEach((x) => x.setAttribute('tabindex', -1));
}

function getUserData() {
  const user = utilsStorage.getUserOnStorage();

  if (user) {
    Sentry.setUser({
      email: user.mail,
      id: user.id,
      username: user.name
    });
  }
}

getUserData();

const SENTRY_NAVIGATION_DEBOUNCE_MS = 100;

function debouncedVueRouterInstrumentation() {
  let timeoutId = null;
  let pendingContext = null;

  return (customStartTransaction, startTransactionOnPageLoad, startTransactionOnLocationChange) => {
    const debouncedStartTransaction = (context) => {
      const isNavigation = context.op === 'navigation';

      if (!isNavigation) {
        return customStartTransaction(context);
      }

      pendingContext = context;

      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (pendingContext) {
          customStartTransaction(pendingContext);
          pendingContext = null;
        }

        timeoutId = null;
      }, SENTRY_NAVIGATION_DEBOUNCE_MS);

      return undefined;
    };

    Sentry.vueRouterInstrumentation(router)(
      debouncedStartTransaction,
      startTransactionOnPageLoad,
      startTransactionOnLocationChange
    );
  };
}

Sentry.init({
  Vue,
  dsn: 'https://d4ebd2a9995c468989176cdecfe71a3c@o1319164.ingest.sentry.io/4504126479597568',
  integrations: [
    new BrowserTracing({
      routingInstrumentation: debouncedVueRouterInstrumentation(),
      // TODO: Ajustar quando for para produção
      tracingOrigins: ['localhost', 'hml-cambiohoje.deeprocket.com.br', /^\//]
    })
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0
});

mixpanel.init('c0b4fce0f9cc40059c199d04f7d35ba9', { debug: true });

sync(store, router);

async function bootstrap() {
  let theme;

  try {
    theme = await loadTheme();
  } catch (error) {
    Sentry.captureException(error);
    theme = {
      name: 'TechCambio',
      data: {
        light: {},
        dark: {}
      }
    };
  }

  Vue.prototype.$appTheme = theme;

  const vuetify = createVuetify(theme);

  const app = new Vue({
    store,
    router,
    vuetify,
    render: (h) => h(App)
  });

  // Tokens de marca acompanham o tema ativo (claro/escuro e troca de tenant).
  installBrandSync(app);

  app.$mount('#app');
}

bootstrap();
